/**
 * Centralized formatting utilities for consistent data display
 *
 * These formatters handle currency, dates, times, and profile data
 * with proper localization and null handling.
 *
 * IMPORTANT: Date formatting uses timezone-safe utilities from date-utils.ts
 * to prevent the "off-by-one-day" bug for users in timezones west of UTC.
 */

import {
  formatDateFever,
  formatDateLong,
  formatDateShort,
  formatDateWithWeekday,
} from './date-utils';

/**
 * Format currency amount with proper localization
 *
 * @param amount - The amount to format
 * @param currency - ISO 4217 currency code (default: EUR)
 * @param options - Formatting options (locale, decimals, notation)
 * @returns Formatted currency string or '-' if amount is null
 *
 * @example
 * formatCurrency(1500, 'EUR') // "€ 1,500.00"
 * formatCurrency(null) // "-"
 * formatCurrency(1500, 'USD') // "$ 1,500.00"
 * formatCurrency(1500, 'EUR', { decimals: 0 }) // "€ 1,500"
 * formatCurrency(1500, 'EUR', { locale: 'de-DE' }) // "1.500 €"
 * formatCurrency(1500000, 'EUR', { notation: 'compact' }) // "€ 1.5M"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string | null | undefined = 'EUR',
  options?: { locale?: string; decimals?: number; notation?: string },
): string {
  if (amount === null || amount === undefined) return '-';

  const validCurrency = currency || 'EUR';
  const locale = options?.locale ?? 'en-US';
  const decimals = options?.decimals;

  return ensureCurrencySpacing(
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: validCurrency,
      ...(decimals !== undefined && {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
      ...(options?.notation && {
        notation: options.notation as 'compact' | 'standard',
      }),
    }).format(amount),
  );
}

/**
 * Ensure there is a non-breaking space between currency symbol and amount.
 * Works for both prefix symbols (€123 → € 123) and suffix symbols (123€ → 123 €).
 * Uses \u00A0 (non-breaking space) to prevent line breaks between symbol and amount.
 */
export function ensureCurrencySpacing(formatted: string): string {
  return formatted
    .replace(/(\p{Sc})(\d)/u, '$1\u00A0$2')
    .replace(/(\d)(\p{Sc})/u, '$1\u00A0$2');
}

/**
 * Format date with localization (TIMEZONE SAFE)
 *
 * @param date - Date string or Date object
 * @param locale - BCP 47 locale identifier (default: en-US)
 * @returns Formatted date string or '-' if date is null
 *
 * @example
 * formatDate('2025-01-15') // "January 15, 2025"
 * formatDate(null) // "-"
 */
export function formatDate(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatDateLong(date, locale);
}

/**
 * Format date with short format (TIMEZONE SAFE)
 *
 * @param date - Date string or Date object
 * @param locale - BCP 47 locale identifier (default: en-US)
 * @returns Formatted date string
 *
 * @example
 * formatShortDate('2025-01-15') // "Jan 15, 2025"
 */
export function formatShortDate(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatDateShort(date, locale);
}

/**
 * Format date with weekday for showtimes (TIMEZONE SAFE)
 *
 * @param dateStr - Date string
 * @returns Formatted date string with weekday
 *
 * @example
 * formatShowtimeDate('2025-01-15') // "Wed, Jan 15, 2025"
 */
export function formatShowtimeDate(dateStr: string | null | undefined): string {
  return formatDateWithWeekday(dateStr, 'en-US');
}

/**
 * Format date in Fever style (e.g., "4-nov-2025") (TIMEZONE SAFE)
 *
 * @param date - Date string or Date object
 * @returns Formatted date string or '-' if date is null
 *
 * @example
 * formatFeverDate('2025-11-04') // "4-nov-2025"
 * formatFeverDate(null) // "-"
 */
export { formatDateFever as formatFeverDate };

/**
 * Format time from HH:MM:SS or HH:MM format
 *
 * @param timeStr - Time string in "HH:MM:SS" or "HH:MM" format
 * @param use12Hour - Whether to use 12-hour format (default: true)
 * @returns Formatted time string
 *
 * @example
 * formatTime('13:00:00') // "1:00 PM"
 * formatTime('09:30', false) // "09:30"
 */
export function formatTime(
  timeStr: string | null | undefined,
  use12Hour: boolean = true,
): string {
  if (!timeStr) return '-';

  // Handle HH:MM:SS format
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours || '0', 10);
  const minute = parseInt(minutes || '0', 10);

  if (!use12Hour) {
    return `${hours}:${minutes}`;
  }

  const date = new Date();
  date.setHours(hour, minute);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format time range (e.g., "13:00-22:00")
 *
 * @param startTime - Start time in "HH:MM:SS" or "HH:MM" format
 * @param endTime - End time in "HH:MM:SS" or "HH:MM" format
 * @returns Formatted time range string or '-' if start time is null
 *
 * @example
 * formatTimeRange('13:00:00', '22:00:00') // "13:00-22:00"
 * formatTimeRange('09:30', '17:00') // "09:30-17:00"
 * formatTimeRange(null, null) // "-"
 */
export function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string {
  if (!startTime) return '-';

  const formatHHMM = (t: string): string => {
    const parts = t.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  if (!endTime) return formatHHMM(startTime);
  return `${formatHHMM(startTime)}-${formatHHMM(endTime)}`;
}

/**
 * Get full name from profile
 *
 * @param profile - Profile object with first_name and last_name
 * @param fallback - Fallback string if name cannot be determined
 * @returns Full name or fallback
 *
 * @example
 * getFullName({ first_name: 'Jane', last_name: 'Doe' }) // "Jane Doe"
 * getFullName({ first_name: null, last_name: null }) // "Unknown"
 */
export function getFullName(
  profile:
    | {
        first_name: string | null;
        last_name: string | null;
      }
    | null
    | undefined,
  fallback: string = 'Unknown',
): string {
  if (!profile || typeof profile !== 'object') return fallback;

  const firstName = profile.first_name ?? null;
  const lastName = profile.last_name ?? null;

  if (firstName && lastName) return `${firstName} ${lastName}`;
  return firstName || lastName || fallback;
}

/**
 * Format reimbursements as text for PDF display
 *
 * @param reimbursements - Array of reimbursement objects
 * @returns Formatted text describing reimbursements
 *
 * @example
 * formatReimbursementsText([
 *   { reimbursement_type: 'per_diem', amount: 50, currency: 'EUR' },
 *   { reimbursement_type: 'materials', amount: 25, currency: 'EUR' }
 * ]) // "€50.00 per show day as a daily allowance\n€25.00 per show day for dance materials"
 */
export function formatReimbursementsText(
  reimbursements:
    | Array<{
        reimbursement_type: string;
        amount: number;
        currency: string;
      }>
    | null
    | undefined,
): string {
  if (!reimbursements || reimbursements.length === 0) {
    return '';
  }

  const descriptions: Record<string, string> = {
    per_diem: 'per show day as a daily allowance',
    materials: 'per show day for dance materials (i.e. pointe shoes)',
    travel: 'for travel expenses',
  };

  return reimbursements
    .map((r) => {
      const amount = formatCurrency(r.amount, r.currency);
      const description =
        descriptions[r.reimbursement_type] || r.reimbursement_type;
      return `${amount} ${description}`;
    })
    .join('\n');
}

/**
 * Format reimbursement type for display
 *
 * @param type - Reimbursement type from database
 * @returns Human-readable label
 *
 * @example
 * formatReimbursementType('per_diem') // "Per Diem"
 * formatReimbursementType('materials') // "Dance Materials"
 */
export function formatReimbursementType(
  type: string | null | undefined,
): string {
  if (!type) return '-';

  const labels: Record<string, string> = {
    per_diem: 'Per Diem',
    travel: 'Travel',
    materials: 'Dance Materials',
  };
  return labels[type] || type;
}

/**
 * Format number with thousands separator
 *
 * @param value - Number to format
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string = 'en-US',
): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format percentage
 *
 * @param value - Decimal value (e.g., 0.15 for 15%)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(0.15) // "15%"
 * formatPercent(0.156, 1) // "15.6%"
 */
export function formatPercent(
  value: number | null | undefined,
  decimals: number = 0,
): string {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(decimals)}%`;
}
